// Local-only mention parser for Discord/Revolt-style syntax.
//
//   Token            Wire form                    Maps to
//   ─────            ─────────                    ───────
//   user mention     <@01H...>                    one user (ULID)
//   role mention     <@&01H...>                   one role (ULID)
//   @everyone        @everyone                    every member of the channel
//   @here            @here                        every online member
//
// Why a *local* parser exists in the E2EE folder: the server canonically
// populates `Message.mentions` from the plaintext body, but in this app the
// body is end-to-end-encrypted before it ever reaches the server. So:
//
//   1. The sender parses locally and attaches `Message.mentions` to the
//      pre-encryption envelope so push routing on the receiver works.
//   2. The receiver re-parses after decrypt and *unions* its own findings
//      with the server-provided list. The server's list cannot be trusted
//      to be complete (it can't see the body) and must not be trusted to be
//      authoritative either (a malicious primary could lie). Union-then-
//      validate is the safe default.
//
// ULID format reminder (Crockford base32, 26 chars, excludes I/L/O/U):
//   [0-9A-HJKMNP-TV-Z]{26}
//
// Mirror of `kaether_for_ios/Stoat/E2EE/MentionParser.swift`.

export interface ParsedMentions {
  readonly userIds: ReadonlySet<string>;
  readonly roleIds: ReadonlySet<string>;
  readonly everyone: boolean;
  readonly here: boolean;
}

export const EMPTY_PARSED_MENTIONS: ParsedMentions = {
  userIds: new Set(),
  roleIds: new Set(),
  everyone: false,
  here: false,
};

// MARK: - Patterns

// Anchored captures so we can pull the ULID without a second regex pass.
// Role mentions intentionally come *before* user mentions in the regex
// alternation order so the `&` is matched as a role and not as part of a
// (failing) user-mention attempt.
const USER_MENTION_PATTERN = /<@([0-9A-HJKMNP-TV-Z]{26})>/g;
const ROLE_MENTION_PATTERN = /<@&([0-9A-HJKMNP-TV-Z]{26})>/g;

// @everyone / @here must be word-bounded so emails like
// `support@everyone.example.com` don't accidentally trigger.
// We use `(?:^|\W)…(?:\W|$)` anchors — the same strategy as iOS's
// NSRegularExpression patterns. The trailing boundary is required so
// `@everyonelarge` does not match.
const EVERYONE_PATTERN = /(?:^|\W)@everyone(?:\W|$)/;
const HERE_PATTERN = /(?:^|\W)@here(?:\W|$)/;

// MARK: - Public API

/**
 * Parse all mention tokens from an E2EE message body.
 *
 * Input is NFC-normalised first so combining-character variants of Crockford
 * base32 letters don't produce false ULID matches.
 *
 * Pure function — no side effects, no I/O.
 */
export function parseMentions(body: string): ParsedMentions {
  if (body.length === 0) {
    return EMPTY_PARSED_MENTIONS;
  }

  // NFC normalise so combining-character sequences don't bypass ULID checks.
  const normalised = body.normalize("NFC");

  return {
    userIds: extractUserIds(normalised),
    roleIds: extractRoleIds(normalised),
    everyone: containsEveryone(normalised),
    here: containsHere(normalised),
  };
}

/** Extract all valid-ULID user mention IDs from `body`. */
export function extractUserIds(body: string): Set<string> {
  return captureAll(body, USER_MENTION_PATTERN);
}

/** Extract all valid-ULID role mention IDs from `body`. */
export function extractRoleIds(body: string): Set<string> {
  return captureAll(body, ROLE_MENTION_PATTERN);
}

/** True when `body` contains `@everyone` with proper word boundaries. */
export function containsEveryone(body: string): boolean {
  return EVERYONE_PATTERN.test(body);
}

/** True when `body` contains `@here` with proper word boundaries. */
export function containsHere(body: string): boolean {
  return HERE_PATTERN.test(body);
}

/**
 * Merge the server-provided `Message.mentions` list with locally-parsed
 * user IDs. Used at receive time when deciding push routing — see the
 * file header for the reasoning.
 */
export function unionUserIds(
  serverProvided: readonly string[] | null | undefined,
  parsed: ParsedMentions,
): Set<string> {
  const out = new Set(parsed.userIds);
  if (serverProvided != null) {
    for (const id of serverProvided) {
      out.add(id);
    }
  }
  return out;
}

// MARK: - Regex plumbing

/** Capture group 1 of every match of `pattern` in `body`. */
function captureAll(body: string, pattern: RegExp): Set<string> {
  const out = new Set<string>();
  // Reset lastIndex for global regex reuse across calls.
  pattern.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(body)) !== null) {
    if (m[1] !== undefined) {
      out.add(m[1]);
    }
  }
  return out;
}
